'use client';

import { useState, useEffect, useCallback } from 'react';
import { m, AnimatePresence, Variants } from 'framer-motion';
import { Quote, ChevronLeft, ChevronRight } from 'lucide-react';
import styles from './Testimonials.module.css';

const testimonials = [
    {
        quote: 'PT Trans Nusa Pasifik sangat membantu distribusi produk kami ke seluruh Indonesia. Pengiriman tepat waktu dan barang selalu dalam kondisi baik.',
        name: 'Ahmad Surya',
        role: 'Direktur Operasional, PT Karya Mandiri',
        initial: 'A',
    },
    {
        quote: 'Layanan cargo laut mereka sangat reliable. Kami sudah menggunakan jasa TNP lebih dari 5 tahun untuk pengiriman antar pulau.',
        name: 'Siti Rahmawati',
        role: 'Supply Chain Manager, CV Nusantara Jaya',
        initial: 'S',
    },
    {
        quote: 'Customer service yang responsif dan harga yang kompetitif. Tracking real-time membuat kami tenang memantau setiap pengiriman.',
        name: 'Budi Prakoso',
        role: 'Owner, Toko Elektronik Sentosa',
        initial: 'B',
    },
    {
        quote: 'Untuk kebutuhan warehousing dan distribusi, TNP adalah pilihan terbaik. Fasilitas gudang mereka modern dan terorganisir dengan baik.',
        name: 'Diana Kusuma',
        role: 'Logistics Manager, PT Global Distribusi',
        initial: 'D',
    },
];

const partners = [
    'PERTAMINA', 'BULOG', 'PELNI', 'ASTRA', 'SEMEN INDONESIA', 'KRAKATAU STEEL', 'PLN', 'UNILEVER'
];

// Duplicate partners for infinite marquee effect
const marqueePartners = [...partners, ...partners];

export default function Testimonials() {
    const [active, setActive] = useState(0);
    const [direction, setDirection] = useState(1);

    const paginate = useCallback((newDirection: number) => {
        setDirection(newDirection);
        setActive((prev) => (prev + newDirection + testimonials.length) % testimonials.length);
    }, []);

    useEffect(() => {
        const interval = setInterval(() => paginate(1), 8000);
        return () => clearInterval(interval);
    }, [paginate]);

    const slideVariants: Variants = {
        enter: (direction: number) => ({
            x: direction > 0 ? 100 : -100,
            opacity: 0,
            scale: 0.95
        }),
        center: {
            zIndex: 1,
            x: 0,
            opacity: 1,
            scale: 1,
            transition: {
                x: { type: 'spring' as const, stiffness: 300, damping: 30 },
                opacity: { duration: 0.4 },
                scale: { duration: 0.4 }
            }
        },
        exit: (direction: number) => ({
            zIndex: 0,
            x: direction < 0 ? 100 : -100,
            opacity: 0,
            scale: 0.95,
            transition: {
                x: { type: 'spring' as const, stiffness: 300, damping: 30 },
                opacity: { duration: 0.4 },
                scale: { duration: 0.4 }
            }
        })
    };

    return (
        <section className={`section ${styles.testimonials}`}>
            <div className={`container ${styles.container}`}>
                <m.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-100px' }}
                    className="section-header"
                >
                    <span className="section-label">Testimoni</span>
                    <h2 className="section-title">Apa Kata Klien Kami</h2>
                    <p className="section-subtitle">
                        Kepercayaan dan kepuasan klien adalah prioritas utama dan nafas bisnis kami.
                    </p>
                </m.div>

                <div className={styles.carouselWrapper}>
                    <div className={styles.carouselContainer}>
                        <AnimatePresence initial={false} custom={direction} mode="wait">
                            <m.div
                                key={active}
                                custom={direction}
                                variants={slideVariants}
                                initial="enter"
                                animate="center"
                                exit="exit"
                                className={styles.slide}
                            >
                                <Quote className={styles.quoteIcon} size={48} strokeWidth={1} />
                                <p className={styles.quoteText}>{testimonials[active].quote}</p>

                                <div className={styles.authorInfo}>
                                    <div className={styles.authorAvatar}>{testimonials[active].initial}</div>
                                    <div className={styles.authorText}>
                                        <div className={styles.authorName}>{testimonials[active].name}</div>
                                        <div className={styles.authorRole}>{testimonials[active].role}</div>
                                    </div>
                                </div>
                            </m.div>
                        </AnimatePresence>
                    </div>

                    <div className={styles.carouselNav}>
                        <button
                            className={styles.navBtn}
                            onClick={() => paginate(-1)}
                            aria-label="Previous testimonial"
                        >
                            <ChevronLeft size={24} />
                        </button>

                        <div className={styles.dots}>
                            {testimonials.map((_, index) => (
                                <button
                                    key={index}
                                    className={`${styles.dot} ${index === active ? styles.dotActive : ''}`}
                                    onClick={() => {
                                        setDirection(index > active ? 1 : -1);
                                        setActive(index);
                                    }}
                                    aria-label={`Go to testimonial ${index + 1}`}
                                />
                            ))}
                        </div>

                        <button
                            className={styles.navBtn}
                            onClick={() => paginate(1)}
                            aria-label="Next testimonial"
                        >
                            <ChevronRight size={24} />
                        </button>
                    </div>
                </div>

                <m.div
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    className={styles.partners}
                >
                    <div className={styles.partnersLabel}>Dipercaya oleh pelanggan tangguh di seluruh Nusantara</div>
                    <div className={styles.marqueeContainer}>
                        <div className={styles.marqueeTrack}>
                            {marqueePartners.map((p, i) => (
                                <span key={`${p}-${i}`} className={styles.partnerLogo}>{p}</span>
                            ))}
                        </div>
                    </div>
                </m.div>
            </div>
        </section>
    );
}
